import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Card, CardContent, Typography } from '@guildeducationinc/recess';
import CheckIcon from '@guildeducationinc/recess/icons/CheckIcon';
import ClearIcon from '@guildeducationinc/recess/icons/ClearIcon';
import WarningOutlinedIcon from '@guildeducationinc/recess/icons/WarningOutlinedIcon';
import { useCurrentClient } from '@src/hooks/useCurrentClient';
import classNames from 'classnames';
import { parseISO } from 'date-fns';
import { getBasePath, getLocalDate, MessageType, TrackableButton, useLocale, } from 'pe-core-frontend';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styles from './IneligibleReasons.module.scss';
const ReasonIcon = ({ reason }) => {
    let icon;
    switch (reason.type.toLowerCase()) {
        case 'danger':
            icon = _jsx(ClearIcon, { color: 'var(--danger-color)', className: styles.reasonIcon });
            break;
        case 'success':
            icon = _jsx(CheckIcon, { color: 'var(--success-color)', className: styles.reasonIcon });
            break;
        default:
            icon = _jsx("span", { className: styles.reasonIcon });
            break;
    }
    return _jsx("div", { className: styles.iconContainer, children: icon });
};
// replace newline with <br> tag
const replaceNewLine = (message) => message.replace(/\n/g, '<br>');
const getDiv = (message) => _jsx("div", { dangerouslySetInnerHTML: { __html: message } });
const getMessage = (clientName, reason, dateFormatter, t) => {
    let message = reason.value;
    const params = reason.params;
    if (!params) {
        return getDiv(replaceNewLine(message));
    }
    const { localize, ...rest } = params;
    const formattedValues = {
        employer: clientName,
        link: (text) => (_jsx(TrackableButton, { btnType: 'link', component: Link, enText: 'Employer policy', tag: 'request_ineligibility_reason', to: '/reimbursement-policy', target: '_blank', children: text })),
    };
    for (const key in rest) {
        let value = rest[key];
        try {
            const formattedDate = dateFormatter(value, { dateStyle: 'medium' });
            if (formattedDate !== 'Invalid Date') {
                if (localize) {
                    value = parseISO(value);
                }
                else {
                    value = formattedDate;
                }
            }
        }
        catch (e) {
            continue;
        }
        finally {
            formattedValues[key] = value;
        }
    }
    if (localize) {
        return t(message, { values: formattedValues });
    }
    for (const key in formattedValues) {
        message = message.replace(new RegExp(`{${key}}`, 'g'), formattedValues[key]);
    }
    return getDiv(replaceNewLine(message));
};
export const IneligibleReasons = ({ paymentRequest, reasons, type }) => {
    const { t, formatToLocalDate } = useLocale();
    const { currentClient } = useCurrentClient();
    const errorRef = useRef(null);
    let isBeforeTermStart;
    if (type === 'eligibility_lock') {
        for (const reason of reasons) {
            if (reason.params?.before_term_start !== undefined) {
                isBeforeTermStart = reason.params.before_term_start;
                break;
            }
        }
    }
    useEffect(() => {
        if (reasons && reasons.length > 0) {
            setTimeout(() => {
                // auto scroll to the top of the page
                if (errorRef.current) {
                    errorRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
                }
            }, 10);
        }
    }, [reasons]);
    return (_jsx(Card, { className: styles.card, children: _jsx(CardContent, { className: styles.content, children: _jsxs("div", { ref: errorRef, children: [_jsxs(Typography, { variant: 'title3', weight: '700', className: 'flex gap-2', children: [_jsx(WarningOutlinedIcon, { size: 24 }), ' ', isBeforeTermStart === false && paymentRequest.termStartDate
                                ? t('You did not meet the following requirement(s) on {termStart}', {
                                    values: { termStart: getLocalDate(paymentRequest.termStartDate) },
                                })
                                : t("You don't currently meet all of {employer}'s requirements.", {
                                    values: { employer: currentClient?.name || '' },
                                })] }), _jsxs("div", { className: styles.reasonsList, children: [reasons.map(reason => (_jsxs("div", { className: styles.reasonRow, children: [_jsx(ReasonIcon, { reason: reason }), ' ', _jsx(Typography, { className: styles.description, variant: 'body1', children: getMessage(currentClient?.name || '', reason, formatToLocalDate, t) })] }, reason.value))), isBeforeTermStart !== undefined && (_jsxs("div", { className: classNames('my-2', styles.reasonRow), children: [_jsx(ReasonIcon, { reason: { type: MessageType.INFO, value: '' } }), ' ', _jsx(Typography, { className: styles.description, variant: 'body1', children: t(isBeforeTermStart ? 'Ineligible now' : 'Ineligible on term start', {
                                            values: {
                                                employer: currentClient?.name || '',
                                            },
                                        }) })] }))] }), _jsx("div", { className: 'flex items-center justify-end', children: _jsx(TrackableButton, { btnType: 'link', enText: 'More details', tag: 'ineligible_reasons', href: `${getBasePath()}/reimbursement-policy`, target: '_blank', rel: 'noopener noreferrer', children: t('More details') }) })] }) }) }));
};
